import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router";
import {
  ArrowLeft,
  Calendar,
  CalendarPlus,
  Camera,
  FlaskConical,
  FolderOpen,
  Home,
  Pill,
  Smile,
  Stethoscope,
  User as UserIcon,
  Wallet,
  ClipboardList,
  History,
  Phone,
} from "lucide-react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@bookdental/ui";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { useFeature } from "@/lib/use-feature";
import { useSetPageHeader } from "@/lib/page-header";
import { useIsMobile } from "@/lib/use-media-query";
import { minutesToTime, timeToMinutes } from "@/lib/agenda-time";
import { AgendaAppointmentDialog } from "@/components/agenda/AgendaAppointmentDialog";
import { AvatarEditor } from "@/components/patients/AvatarEditor";
import { BirthdayBadge } from "@/components/patients/BirthdayBadge";
import { PatientHomeTab } from "@/components/patients/tabs/PatientHomeTab";
import { PatientDataTab } from "@/components/patients/tabs/PatientDataTab";
import { AbaAnamnese } from "@/components/patients/anamnese/AbaAnamnese";
import { SelosDaFicha } from "@/components/patients/anamnese/SelosDeAlerta";
import { SelosDoCadastro } from "@/components/patients/SelosDoCadastro";
import { useConfiguracaoDePacientes } from "@/lib/use-configuracao-de-pacientes";
import type { AbaConfiguravel } from "@bookdental/schemas";
import { PatientOdontogramaTab } from "@/components/patients/tabs/PatientOdontogramaTab";
import { AbaTratamentos } from "@/components/patients/tratamentos/AbaTratamentos";
import { PatientAppointmentsTab } from "@/components/patients/tabs/PatientAppointmentsTab";
import { PatientFinanceiroTab } from "@/components/patients/tabs/PatientFinanceiroTab";
import { PatientHistoricoTab } from "@/components/patients/tabs/PatientHistoricoTab";
import { PatientProtesesTab } from "@/components/patients/tabs/PatientProtesesTab";
import { CoroaDentariaIcon } from "@/components/icons/CoroaDentariaIcon";
import { useFluxoNovoPedido } from "@/components/prosthesis/FluxoNovoPedido";
import { useAuth } from "@/lib/auth";
import { useSoLeitura } from "@/components/organization/painel/OrganizacaoEmFoco";
import { PatientArquivosTab } from "@/components/patients/tabs/PatientArquivosTab";
import { BarraDeAtendimento } from "@/components/patients/BarraDeAtendimento";
import { getPatientColor, patientGradient } from "@/lib/patient-color";
import { useTourDaPagina } from "@/components/product-tour/PageTourEntry";
import { getPatientDetailTourSteps } from "@/lib/product-tour/page-tour-steps";

export function PatientDetail() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { user } = useAuth();
  // Anamnese/Odontograma/Tratamentos/Financeiro e as ações rápidas correspondentes são reservados
  // ao plano pago — some da UI pra quem não tem a feature, não é bloqueio no clique.
  const isPaidPlan = useFeature("patient.advancedTabs");
  // Arquivos tem feature própria: é a única dessas abas com implementação real ponta a ponta.
  const temArquivos = useFeature("patient.files");
  const temTratamentos = useFeature("patient.treatmentPlans");
  const temAnamnese = useFeature("patient.anamnese");
  // Ficha de outra clínica pelo acesso pessoal: os atalhos que ainda são "em breve" saem.
  const soLeitura = useSoLeitura();
  // Modo atendimento: a agenda manda pra cá com o id da consulta em curso. A ficha vira a tela
  // de trabalho do dentista — faixa do atendimento no topo e os exames já abertos.
  const atendimentoId = new URLSearchParams(location.search).get("atendimento");
  // Aba pedida na URL — é como "Ver atendimento", no diálogo da consulta, chega direto no Histórico
  // em vez de largar o usuário no Início da ficha pra procurar.
  const abaPedida = new URLSearchParams(location.search).get("aba");
  // Mesma chave que gateia "Prótese" no menu — quem não enxerga pedidos não ganha a aba.
  const podeVerProteses = !!user?.activeOrganization?.permissions?.["prosthesis.view"];
  const utils = trpc.useUtils();
  const podePedirProtese = !!user?.activeOrganization?.permissions?.["prosthesis.manage"];
  const podeEditarPaciente = !!user?.activeOrganization?.permissions?.["patients.manage"];
  const podeMontarPlano = temTratamentos && !!user?.activeOrganization?.permissions?.["treatments.manage"];
  const fluxoDoPedido = useFluxoNovoPedido({ onConcluido: () => utils.prosthesis.invalidate() });
  // Cada "Criar plano" muda o número: a aba Tratamentos abre um plano novo.
  const [pedidoDePlano, setPedidoDePlano] = useState(0);
  // Entrar em atendimento abre direto nos exames — é o que o dentista precisa na cadeira.
  const [tab, setTab] = useState(abaPedida ?? (atendimentoId ? "arquivos" : "inicio"));
  // Precisa de efeito, e não só do valor inicial: vindo da PRÓPRIA ficha (o diálogo da consulta
  // abre em cima dela) muda só a query string, a rota não remonta e o estado inicial não roda de
  // novo — a aba pedida era ignorada justamente no caminho mais comum.
  useEffect(() => {
    if (abaPedida) setTab(abaPedida);
  }, [abaPedida]);
  // Abas que a clínica escondeu em Configurações › Pacientes, por cima das regras de recurso e
  // permissão. Chegar numa delas (URL, atendimento) cai no Início.
  const abasOcultas = useConfiguracaoDePacientes().ficha.abasOcultas;
  const aba = (a: AbaConfiguravel) => !abasOcultas.includes(a);
  useEffect(() => {
    if ((abasOcultas as string[]).includes(tab)) setTab("inicio");
  }, [abasOcultas, tab]);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);

  const query = trpc.patient.getById.useQuery({ id: id! }, { enabled: !!id });
  const nextAppointmentQuery = trpc.appointment.getNextByPatient.useQuery(
    { patientId: id! },
    { enabled: !!id },
  );
  const agendaSettingsQuery = trpc.settings.getAgenda.useQuery();

  const update = trpc.patient.update.useMutation({
    onSuccess: () => utils.patient.getById.invalidate({ id: id! }),
  });

  const patient = query.data as any | undefined;

  const saveAvatar = async (dataUrl: string | null) => {
    await update.mutateAsync({ id: patient.id, data: { avatarUrl: dataUrl } });
    toast.success(dataUrl ? "Foto atualizada" : "Foto removida");
  };

  useTourDaPagina(getPatientDetailTourSteps(location.pathname));
  // No celular o título aparece logo acima do cartão do paciente, que já mostra o nome em
  // destaque — ali fica só "Paciente". No desktop o título mora na barra de cima e segue com o nome.
  const isMobile = useIsMobile();
  useSetPageHeader(
    patient
      ? {
          title: isMobile ? "Paciente" : `Paciente: ${patient.name}`,
        }
      : { title: "Paciente", subtitle: "Carregando..." },
    [patient?.id, patient?.name, location.pathname, isMobile],
  );

  if (query.isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  if (!query.data || !patient) {
    return (
      <div className="mx-auto max-w-2xl space-y-4 text-center py-20">
        <p className="text-sm text-zinc-500">Paciente não encontrado.</p>
        <Link to="/pacientes" className="text-sm text-brand-700 hover:underline">
          Voltar para a lista
        </Link>
      </div>
    );
  }

  const initial = patient.name.charAt(0).toUpperCase();
  const color = getPatientColor(patient.name);
  const ageYears = calcAge(patient.birthDate);
  const firstName = patient.name.split(" ")[0];
  const nextAppt = nextAppointmentQuery.data;

  const refetch = () => utils.patient.getById.invalidate({ id: id! });

  const openScheduleDialog = () => setScheduleOpen(true);
  const criarPlano = () => {
    setTab("tratamentos");
    setPedidoDePlano((n) => n + 1);
  };

  const handleAppointmentSaved = () => {
    utils.appointment.invalidate();
  };

  const agendaSettings = agendaSettingsQuery.data;
  const scheduleDialogInitial = agendaSettings
    ? {
        date: new Date(),
        startTime: agendaSettings.startHour,
        endTime: minutesToTime(
          Math.min(
            timeToMinutes(agendaSettings.startHour) + agendaSettings.defaultAppointmentDuration,
            timeToMinutes(agendaSettings.endHour),
          ),
        ),
        patientId: patient.id,
        patientName: patient.name,
        patientPhone: patient.phone ?? "",
      }
    : null;

  return (
    <div className="mx-auto w-full max-w-[1600px] space-y-4 pb-10">
      {atendimentoId && <BarraDeAtendimento appointmentId={atendimentoId} />}

      {/* Hero compacto */}
      <section className="relative overflow-hidden rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm sm:p-5">
        <div
          className="absolute inset-y-0 left-0 w-1"
          style={{ background: patientGradient(color) }}
          aria-hidden
        />
        <div className="grid gap-4 lg:grid-cols-[1fr_auto] lg:gap-6">
          {/* Esquerda: back + avatar + identidade + contato */}
          <div className="flex items-start gap-3">
            <Link
              to="/pacientes"
              className="mt-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-zinc-200 bg-white text-zinc-500 transition-colors hover:border-zinc-300 hover:bg-zinc-50 hover:text-zinc-900"
              aria-label="Voltar para pacientes"
              title="Voltar para pacientes"
            >
              <ArrowLeft className="h-4 w-4" />
            </Link>
            <button
              type="button"
              onClick={() => setAvatarOpen(true)}
              disabled={!podeEditarPaciente}
              className="group relative shrink-0 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:cursor-default"
              aria-label={podeEditarPaciente ? "Editar foto do paciente" : undefined}
            >
              {patient.avatarUrl ? (
                <img
                  src={patient.avatarUrl}
                  alt=""
                  className="h-16 w-16 rounded-full object-cover ring-2 ring-white transition-transform group-hover:scale-[1.02]"
                  style={{ boxShadow: `0 4px 12px ${color.solid}33` }}
                />
              ) : (
                <div
                  className="flex h-16 w-16 items-center justify-center rounded-full text-xl font-bold text-white ring-2 ring-white transition-transform group-hover:scale-[1.02]"
                  style={{
                    background: patientGradient(color),
                    boxShadow: `0 4px 12px ${color.solid}33`,
                  }}
                >
                  {initial}
                </div>
              )}
              <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/0 text-white opacity-0 transition-all group-hover:bg-black/30 group-hover:opacity-100">
                <Camera className="h-4 w-4" />
              </span>
            </button>

            <div className="min-w-0 flex-1 space-y-1">
              {/* Linha 1: nome + status */}
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-xl font-semibold text-zinc-900">{patient.name}</h2>
                <SelosDaFicha patientId={patient.id} />
                <SelosDoCadastro patient={patient as never} onAbrirAba={setTab} />
                {patient.active ? (
                  <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700 ring-1 ring-emerald-200">
                    Ativo
                  </span>
                ) : (
                  <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-medium text-zinc-500">
                    Inativo
                  </span>
                )}
              </div>

              {/* Linha 2: idade + badge de aniversário próximo */}
              {(ageYears !== null || patient.birthDate) && (
                <div className="flex flex-wrap items-center gap-2">
                  {ageYears !== null && (
                    <span className="text-sm text-zinc-600 dark:text-muted-foreground">
                      {ageYears} anos
                    </span>
                  )}
                  <BirthdayBadge birthDate={patient.birthDate} />
                </div>
              )}

              {/* Linha 3: telefone + WhatsApp inline */}
              {patient.phone || patient.whatsapp ? (
                <div className="flex items-center gap-2 pt-0.5">
                  <Phone className="h-3.5 w-3.5 text-zinc-400" />
                  <span className="text-sm text-zinc-700">{formatPhone(patient.phone || patient.whatsapp)}</span>
                  <a
                    href={whatsappUrl(patient.whatsapp || patient.phone, firstName)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500 text-white transition-colors hover:bg-emerald-600"
                    aria-label="Enviar mensagem no WhatsApp"
                    title="Enviar WhatsApp"
                  >
                    <WhatsAppIcon className="h-3.5 w-3.5" />
                  </a>
                </div>
              ) : null}

              {/* Linha 4: próxima consulta chip */}
              {nextAppt && (
                <div className="pt-1">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-2.5 py-1 text-[11px] font-medium text-brand-700 ring-1 ring-brand-200">
                    <Calendar className="h-3 w-3" />
                    Próxima: {format(new Date(nextAppt.date), "EEE dd MMM", { locale: ptBR })} · {nextAppt.startTime}
                    {nextAppt.procedure ? ` · ${nextAppt.procedure}` : ""}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Direita: ações rápidas (4 botões) */}
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-2 xl:grid-cols-4">
            <QuickAction
              icon={<CalendarPlus className="h-4 w-4" />}
              label="Agendar consulta"
              onClick={openScheduleDialog}
              dataTour="paciente-detalhe-agendar"
            />
            {podePedirProtese && (
              <QuickAction
                icon={<CoroaDentariaIcon className="h-4 w-4" />}
                label="Pedir prótese"
                onClick={() => fluxoDoPedido.abrirParaPaciente({ id: patient.id, name: patient.name })}
              />
            )}
            {podeMontarPlano && (
              <QuickAction
                icon={<ClipboardList className="h-4 w-4" />}
                label="Criar plano"
                onClick={criarPlano}
              />
            )}
            {isPaidPlan && !soLeitura && (
              <>
                <QuickAction
                  icon={<FlaskConical className="h-4 w-4" />}
                  label="Solicitar exames"
                  onClick={() => toast.info("Solicitação de exames — em breve")}
                />
                <QuickAction
                  icon={<ClipboardList className="h-4 w-4" />}
                  label="Novo tratamento"
                  onClick={() => toast.info("Novo tratamento — em breve")}
                />
                <QuickAction
                  icon={<Pill className="h-4 w-4" />}
                  label="Receita médica"
                  onClick={() => toast.info("Receita médica — em breve")}
                />
              </>
            )}
          </div>
        </div>
      </section>

      {/* Tabs: underline style, full width, sticky */}
      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <div className="sticky top-16 z-10 -mx-4 overflow-x-auto border-b border-zinc-200 bg-white/90 px-4 backdrop-blur-sm dark:border-border dark:bg-card/90 sm:-mx-6 sm:px-6 md:-mx-8 md:px-8">
          <TabsList data-tour="paciente-detalhe-abas" className="h-auto w-max justify-start rounded-none border-0 bg-transparent p-0 gap-0">
            <TabTrigger value="inicio" icon={<Home className="h-4 w-4" />}>Início</TabTrigger>
            <TabTrigger value="dados" icon={<UserIcon className="h-4 w-4" />}>Dados</TabTrigger>
            {temAnamnese && aba("anamnese") && (
              <TabTrigger value="anamnese" icon={<Stethoscope className="h-4 w-4" />}>Anamnese</TabTrigger>
            )}
            {isPaidPlan && aba("odontograma") && (
              <TabTrigger value="odontograma" icon={<Smile className="h-4 w-4" />}>Odontograma</TabTrigger>
            )}
            {temTratamentos && aba("tratamentos") && (
              <TabTrigger value="tratamentos" icon={<ClipboardList className="h-4 w-4" />}>Tratamentos</TabTrigger>
            )}
            {aba("consultas") && <TabTrigger value="consultas" icon={<Calendar className="h-4 w-4" />}>Consultas</TabTrigger>}
            {podeVerProteses && aba("proteses") && (
              <TabTrigger value="proteses" icon={<CoroaDentariaIcon className="h-4 w-4" />}>Próteses</TabTrigger>
            )}
            {temArquivos && aba("arquivos") && (
              <TabTrigger value="arquivos" icon={<FolderOpen className="h-4 w-4" />}>Arquivos</TabTrigger>
            )}
            {isPaidPlan && aba("financeiro") && (
              <TabTrigger value="financeiro" icon={<Wallet className="h-4 w-4" />}>Financeiro</TabTrigger>
            )}
            {aba("historico") && <TabTrigger value="historico" icon={<History className="h-4 w-4" />}>Histórico</TabTrigger>}
          </TabsList>
        </div>

        <TabsContent value="inicio" className="mt-6 rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm sm:p-6 md:p-8">
          <PatientHomeTab
            patientId={patient.id}
            paciente={{ name: patient.name, phone: patient.phone, whatsapp: patient.whatsapp, landline: patient.landline, email: patient.email }}
            onScheduleAppointment={openScheduleDialog}
            onPedirProtese={podePedirProtese ? () => fluxoDoPedido.abrirParaPaciente({ id: patient.id, name: patient.name }) : undefined}
            onCriarPlano={podeMontarPlano ? criarPlano : undefined}
            onIrParaAba={setTab}
          />
        </TabsContent>
        <TabsContent value="dados" className="mt-6">
          <PatientDataTab patient={patient} onSaved={refetch} />
        </TabsContent>
        {temAnamnese && (
          <TabsContent value="anamnese" className="mt-6 rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm sm:p-6 md:p-8">
            <AbaAnamnese paciente={{ id: patient.id, name: patient.name, phone: patient.phone, email: patient.email, birthDate: patient.birthDate }} />
          </TabsContent>
        )}
        {isPaidPlan && (
          <TabsContent value="odontograma" className="mt-6 rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm sm:p-6 md:p-8">
            <PatientOdontogramaTab patientId={patient.id} />
          </TabsContent>
        )}
        {temTratamentos && (
          <TabsContent value="tratamentos" className="mt-6 rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm sm:p-6 md:p-8">
            <AbaTratamentos patientId={patient.id} nascimento={patient.birthDate} sexo={patient.sex} pedidoDeNovo={pedidoDePlano} />
          </TabsContent>
        )}
        <TabsContent value="consultas" className="mt-6 rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm sm:p-6 md:p-8">
          <PatientAppointmentsTab patientId={patient.id} />
        </TabsContent>
        {podeVerProteses && (
          <TabsContent value="proteses" className="mt-6 rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm sm:p-6 md:p-8">
            <PatientProtesesTab
              patientId={patient.id}
              patientName={patient.name}
              patientPhone={patient.phone}
            />
          </TabsContent>
        )}
        {temArquivos && (
          <TabsContent value="arquivos" className="mt-6 rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm sm:p-6 md:p-8">
            <PatientArquivosTab patientId={patient.id} />
          </TabsContent>
        )}
        {isPaidPlan && (
          <TabsContent value="financeiro" className="mt-6 rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm sm:p-6 md:p-8">
            <PatientFinanceiroTab patientId={patient.id} />
          </TabsContent>
        )}
        <TabsContent value="historico" className="mt-6 rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm sm:p-6 md:p-8">
          <PatientHistoricoTab patientId={patient.id} onIrParaAba={setTab} />
        </TabsContent>
      </Tabs>
      {fluxoDoPedido.elementos}

      <AvatarEditor
        open={avatarOpen}
        onClose={() => setAvatarOpen(false)}
        current={patient.avatarUrl || null}
        patientName={patient.name}
        onSave={saveAvatar}
      />

      {scheduleOpen && agendaSettings && scheduleDialogInitial && (
        <AgendaAppointmentDialog
          open
          mode="create"
          initial={scheduleDialogInitial}
          settings={agendaSettings}
          onClose={() => setScheduleOpen(false)}
          onSaved={handleAppointmentSaved}
        />
      )}
    </div>
  );
}

// ─── Subcomponentes ──────────────────────────────────────

function TabTrigger({
  value,
  icon,
  children,
}: {
  value: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <TabsTrigger
      value={value}
      className="relative h-11 shrink-0 gap-2 rounded-none border-0 border-b-2 border-transparent bg-transparent px-4 text-sm font-medium text-zinc-600 shadow-none transition-colors hover:bg-zinc-100/70 hover:text-zinc-900 data-[state=active]:border-brand-600 data-[state=active]:bg-transparent data-[state=active]:text-brand-700 data-[state=active]:shadow-none data-[state=active]:font-semibold dark:text-muted-foreground dark:hover:bg-muted/50 dark:hover:text-foreground dark:data-[state=active]:border-primary dark:data-[state=active]:text-primary"
    >
      {icon}
      <span className="hidden sm:inline">{children}</span>
    </TabsTrigger>
  );
}

function QuickAction({
  icon,
  label,
  onClick,
  dataTour,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  dataTour?: string;
}) {
  return (
    <button
      type="button"
      data-tour={dataTour}
      onClick={onClick}
      className="flex flex-col items-center justify-center gap-1 rounded-lg border border-zinc-200 bg-white px-3 py-2.5 text-[11px] font-medium leading-tight text-zinc-700 transition-colors hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700"
    >
      <span className="text-brand-600">{icon}</span>
      <span className="text-center">{label}</span>
    </button>
  );
}

// ─── Helpers ─────────────────────────────────────────────

function formatPhone(v: string | null): string {
  if (!v) return "";
  const d = v.replace(/\D/g, "");
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return v;
}

function whatsappUrl(phone: string, firstName: string): string {
  const digits = phone.replace(/\D/g, "");
  const withCountry = digits.startsWith("55") ? digits : `55${digits}`;
  const message = encodeURIComponent(`Olá ${firstName}, aqui é da clínica.`);
  return `https://wa.me/${withCountry}?text=${message}`;
}

/** Idade em anos completos. Usa partes UTC (Prisma salva em UTC midnight). */
function calcAge(birthDate: Date | string | null | undefined): number | null {
  if (!birthDate) return null;
  const d = new Date(birthDate);
  if (Number.isNaN(d.getTime())) return null;
  const by = d.getUTCFullYear();
  const bm = d.getUTCMonth();
  const bd = d.getUTCDate();
  const today = new Date();
  let age = today.getFullYear() - by;
  const hadBdayThisYear =
    today.getMonth() > bm || (today.getMonth() === bm && today.getDate() >= bd);
  if (!hadBdayThisYear) age--;
  return age >= 0 ? age : null;
}

function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
    </svg>
  );
}
